/*
 *
 * Copyright (c) 1999-2021 Luciad All Rights Reserved.
 *
 * Luciad grants you ("Licensee") a non-exclusive, royalty free, license to use,
 * modify and redistribute this software in source and binary code form,
 * provided that i) this copyright notice and license appear on all copies of
 * the software; and ii) Licensee does not utilize the software in a manner
 * which is disparaging to Luciad.
 *
 * This software is provided "AS IS," without a warranty of any kind. ALL
 * EXPRESS OR IMPLIED CONDITIONS, REPRESENTATIONS AND WARRANTIES, INCLUDING ANY
 * IMPLIED WARRANTY OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR
 * NON-INFRINGEMENT, ARE HEREBY EXCLUDED. LUCIAD AND ITS LICENSORS SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE AS A RESULT OF USING, MODIFYING
 * OR DISTRIBUTING THE SOFTWARE OR ITS DERIVATIVES. IN NO EVENT WILL LUCIAD OR ITS
 * LICENSORS BE LIABLE FOR ANY LOST REVENUE, PROFIT OR DATA, OR FOR DIRECT,
 * INDIRECT, SPECIAL, CONSEQUENTIAL, INCIDENTAL OR PUNITIVE DAMAGES, HOWEVER
 * CAUSED AND REGARDLESS OF THE THEORY OF LIABILITY, ARISING OUT OF THE USE OF
 * OR INABILITY TO USE SOFTWARE, EVEN IF LUCIAD HAS BEEN ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGES.
 */
import {Control} from "@luciad/ria-sample-common/control/Control";
import {FormatUtil} from "@luciad/ria-sample-common/FormatUtil";
import {
  DEACTIVATED_EVENT,
  ENABLED_CHANGE_EVENT,
  MEASUREMENT_CHANGED_EVENT, Ruler3DController
} from "./Ruler3DController";
import {MeasureType} from "./MeasureType";

const measureTypes = [{
  text: "Délka",
  value: MeasureType.SIMPLE
}, {
  text: "Pravoúhlé",
  value: MeasureType.ORTHO,
  only3D: true
}, {
  text: "Plocha",
  value: MeasureType.AREA
}, {
  text: "Výška",
  value: MeasureType.HEIGHT,
  only3D: true
}];

export class Ruler3DPanel extends Control {

  constructor(map, controller, options) {
    options = options || {};
    options.title = options.title || "3D MĚŘENÍ";
    options.icon = options.icon || "lcdIconHeight";
    super(map, options);

    this._nodes = {
      btnController: null,
      measurementTypeButtons: [],
      infoDistance: null,
      infoArea: null,
      container_infoDistance: null,
      container_infoArea: null
    };

    if (!controller || !(controller instanceof Ruler3DController)) {
      throw new Error("Ruler3DPanel.constructor: you must specify a controller");
    }
    this._eventHandles = this._listenToController(controller, this._nodes);
    this.init(map, options);

    this._setMeasureType(MeasureType.SIMPLE);
  }

  _createDOMStructure(options) {
    super._createDOMStructure(options);
    this._panelNode.classList.add("ruler-container");
    this._addControllerStateButton("Controller");
    this._addModeButtons();
    this._createInfo("infoDistance", "Celková délka:");
    this._createInfo("infoArea", "Celková plocha:");
  }

  _createInfo(nodeName, title) {
    const container = document.createElement('div');
    this._nodes["container_" + nodeName] = container;
    container.classList.add("panelInfo");

    const domLabel = document.createElement('div');
    domLabel.classList.add("panelInfoColumn");
    domLabel.innerHTML = title;
    const nodeInfoValue = document.createElement('div');
    nodeInfoValue.innerHTML = "-";
    nodeInfoValue.classList.add("panelInfoColumn");

    container.appendChild(domLabel);
    container.appendChild(nodeInfoValue);
    this._panelNode.appendChild(container);
    this._nodes[nodeName] = nodeInfoValue;
  }

  _addModeButtons() {
    const changeMeasureType = this._setMeasureType.bind(this);
    const self = this;

    this._addSectionTitle("Mód měření");
    const buttonGroup = document.createElement('div');
    buttonGroup.classList.add("panelButtonGroup");

    measureTypes.forEach(function(modeInfo) {
      const modeBtn = document.createElement('button');
      modeBtn.innerHTML = modeInfo.text;

      modeBtn.classList.add("btn", "panelButton");
      if (modeInfo.only3D && !self._map.is3D()) {
        modeBtn.disabled = true;
      }

      // hook click event handler
      modeBtn.addEventListener("click", function() {
        changeMeasureType(modeInfo.value);
      }, false);

      buttonGroup.appendChild(modeBtn);

      modeBtn.setAttribute('measurementMode', modeInfo.value);
      self._nodes.measurementTypeButtons.push(modeBtn);
    });

    this._panelNode.appendChild(buttonGroup);
  }

  _setMeasureType(activeMode) {
    this._controller.setMeasureType(activeMode);

    // highlight the active measure type button
    this._nodes.measurementTypeButtons.forEach(function(btn) {
      const mode = btn.getAttribute('measurementMode');
      if (parseInt(mode) === activeMode) {
        btn.classList.add("active");
      } else {
        btn.classList.remove("active");
      }
    });

    // show only relevant information
    const infoDistanceStateOn = (activeMode !== MeasureType.HEIGHT);
    setHiddenState(this._nodes.container_infoDistance, infoDistanceStateOn);

    const infoAreaStateOn = (activeMode === MeasureType.AREA);
    setHiddenState(this._nodes.container_infoArea, infoAreaStateOn);

    function setHiddenState(node, stateOn) {
      if (stateOn) {
        node.classList.remove("hidden");
      } else {
        node.classList.add("hidden");
      }
    }
  }

  _addControllerStateButton() {
    const btnNode = document.createElement("div");
    btnNode.classList.add("panelButton");
    btnNode.innerHTML = getButtonDescription(this._controller.enabled);

    const divState = document.createElement("div");
    divState.classList.add("stateInfo");
    divState.innerHTML = getCurrentStateText(this._controller.enabled);

    btnNode.addEventListener("click", () => {
      this._controller.enabled = !this._controller.enabled;
      btnNode.innerHTML = getButtonDescription(this._controller.enabled);

      divState.innerHTML = getCurrentStateText(this._controller.enabled);
    }, false);


    const domNode = document.createElement("div");
    domNode.classList.add("panelButtonGroup");
    domNode.appendChild(btnNode);
    domNode.appendChild(divState);

    this._panelNode.appendChild(domNode);
    this._nodes.btnController = btnNode;

    function getButtonDescription(state) {
      return (!state ? "Zapnout" : "Vypnout");
    }

    function getCurrentStateText(state) {
      return " měření je " + (state ? "ZAPNUTÉ" : "VYPNUTÉ");
    }
  }

  _addSectionTitle(title) {
    const domElement = document.createElement('div');
    domElement.innerHTML = title;
    domElement.classList.add("panelTitle");
    this._panelNode.appendChild(domElement);
  }

  _listenToController(controller, nodes) {
    const options = {};
    options.measureType = MeasureType.SIMPLE;

    const changedEventHandle = controller.on(MEASUREMENT_CHANGED_EVENT, function() {
      const totals = controller.getMeasurements().totals;
      displayValue(nodes.infoDistance, FormatUtil.distance(totals.length));
      displayValue(nodes.infoArea, FormatUtil.area(totals.area));
    });

    const resetValues = () => {
      displayValue(nodes.infoDistance, 0);
      displayValue(nodes.infoArea, 0);
    };

    const deactivatedEventHandle = controller.on(DEACTIVATED_EVENT, resetValues);
    const disabledEventHandle = controller.on(ENABLED_CHANGE_EVENT, resetValues);

    this._controller = controller;

    return [
     changedEventHandle,
     deactivatedEventHandle,
     disabledEventHandle
    ];
  }

  destroy() {
    if (this._eventHandles) {
      this._eventHandles.forEach(handle => handle && handle.remove());
    }
  }
}

function displayValue(node, value) {
  if (node) {
    node.innerHTML = value || "-";
  }
}